import { logDataToConsole, logError } from "../utils/customLogger";

type Store = Record<string, any>;

/**
 * Singleton store for user session data.
 * Replaces the React Context-based UserGeneralInfoContext.
 *
 * Usage:
 *   import { UserStore, UserGeneralInfoContextKeys } from "../store/UserGeneralInfoStore";
 *   UserStore.getItem(UserGeneralInfoContextKeys.USER_ID);
 *   UserStore.setItem(UserGeneralInfoContextKeys.FEATURES, [...]);
 */
class UserGeneralInfoStore {
  private static instance: UserGeneralInfoStore;
  private store: Store = {};

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  static getInstance(): UserGeneralInfoStore {
    if (!UserGeneralInfoStore.instance) {
      UserGeneralInfoStore.instance = new UserGeneralInfoStore();
    }
    return UserGeneralInfoStore.instance;
  }

  getItem(key: string): any {
    try {
      return this.store[key] ?? null;
    } catch (error) {
      logError(error as Error, "Error getting item from store");
      return null;
    }
  }

  setItem(key: string, value: any): void {
    try {
      this.store[key] = value;
    } catch (error) {
      logError(error as Error, "Error setting item in store");
    }
  }

  removeItem(key: string): void {
    try {
      delete this.store[key];
    } catch (error) {
      logError(error as Error, "Error removing item from store");
    }
  }

  clearAll(): void {
    try {
      this.store = {};
    } catch (error) {
      logError(error as Error, "Error clearing store");
    }
  }

  containsItem(key: string): boolean {
    try {
      return key in this.store;
    } catch (error) {
      logError(error as Error, "Error checking item in store");
      return false;
    }
  }

  updateItem(key: string, newValue: any): void {
    try {
      const existingItem = this.store[key];
      if (existingItem !== undefined) {
        this.store[key] = { ...existingItem, ...newValue };
      } else {
        logDataToConsole(`Item with key "${key}" does not exist in store.`);
      }
    } catch (error) {
      logError(error as Error, "Error updating item in store");
    }
  }

  getAllItems(): Store {
    try {
      return { ...this.store };
    } catch (error) {
      logError(error as Error, "Error getting all items from store");
      return {};
    }
  }

  getStoreLength(): number {
    try {
      return Object.keys(this.store).length;
    } catch (error) {
      logError(error as Error, "Error getting store length");
      return 0;
    }
  }
}

// Export singleton instance
export const UserStore = UserGeneralInfoStore.getInstance();

// Re-export the keys enum for convenience
export { UserGeneralInfoContextKeys } from "../../shared/enums/userGeneralInfoContextKeys";
